import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { EmailHtmlIssue, EmailImageResult, EmailPreviewDevice } from 'src/app/models/communication/email-html-import';
import { HtmlCodeEditorComponent } from 'src/app/modules/shared/components/html-code-editor/html-code-editor.component';
import { DataService } from 'src/app/services/data.service';
import { SiteService } from 'src/app/services/site.service';

/**
 * Expert (HTML) mode editor used by both the template and campaign screens: code editor,
 * live sandboxed preview, substitution-field insertion, and the server-side import that
 * re-hosts images and lints. The parent owns the HTML string via [(html)], gives this
 * component a fixed height, and hosts the toolbar that calls reimportImages() /
 * openImagePicker().
 */
@Component({
	selector: 'app-expert-html-editor',
	templateUrl: './expert-html-editor.component.html',
	styleUrls: ['./expert-html-editor.component.scss']
})
export class ExpertHtmlEditorComponent implements OnInit, OnDestroy {
	@Input() html: string = '';
	@Output() htmlChange = new EventEmitter<string>();
	/** Fires on any user-driven change; parents use it to set their dirty flag. */
	@Output() changed = new EventEmitter<void>();

	@ViewChild(HtmlCodeEditorComponent) codeEditor: HtmlCodeEditorComponent;
	@ViewChild('imageInput') imageInput: ElementRef<HTMLInputElement>;

	public issues: EmailHtmlIssue[] = [];
	public images: EmailImageResult[] = [];
	public importing: boolean = false;
	public device: EmailPreviewDevice = 'desktop';

	private lint$ = new Subject<string>();
	private lintSub: Subscription;

	constructor(private dataService: DataService, private siteService: SiteService) { }

	ngOnInit(): void {
		// Lint while typing, debounced; image results from the last import are kept. A result is
		// applied only if the editor still holds the HTML it was run on: opening or pasting a file
		// lints the raw text and imports it at the same time, and a lint that lands after the import
		// would otherwise replace the import's issues with ones it already fixed.
		this.lintSub = this.lint$.pipe(
			debounceTime(600),
			distinctUntilChanged(),
			switchMap(html => (html?.trim()
				? this.dataService.lintEmailHtml(html).pipe(catchError(() => of(null)))
				: of([] as EmailHtmlIssue[])
			).pipe(map(issues => ({ html, issues }))))
		).subscribe(({ html, issues }) => {
			if (issues && html === this.html) this.issues = issues;
		});
		if (this.html?.trim()) this.lint$.next(this.html);
	}

	ngOnDestroy(): void {
		this.lintSub?.unsubscribe();
	}

	get errorCount(): number {
		return this.issues.filter(i => i.severity === 'error').length;
	}

	onValueChange(value: string) {
		this.html = value;
		this.htmlChange.emit(value);
		this.changed.emit();
		this.lint$.next(value);
	}

	onPasted(fullDocument: string) {
		this.import(fullDocument);
	}

	onFileOpened(file: { name: string; html: string }) {
		this.import(file.html);
	}

	reimportImages() {
		if (this.html?.trim()) this.import(this.html);
	}

	insertField(field: string) {
		this.codeEditor?.insertAtCursor(field);
	}

	openImagePicker() {
		this.imageInput?.nativeElement.click();
	}

	/** Upload a single image to One Morning storage and insert an email-safe img tag at the cursor (replacing any selection). */
	onImageFile(event: Event) {
		const input = event.target as HTMLInputElement;
		const file = input.files?.[0];
		input.value = '';
		if (!file) return;
		if (!file.type.startsWith('image/')) {
			this.siteService.setMessage('Only image files can be uploaded here.', 'warn');
			return;
		}
		this.dataService.uploadFile(crypto.randomUUID(), file).subscribe({
			next: (res: any) => {
				// Stored file names contain a space; email clients can't load an address with one.
				const url: string = (res?.filePath ?? '').replace(/ /g, '%20');
				this.codeEditor?.insertAtCursor(`<img src="${url}" alt="" width="600" style="display:block; max-width:100%; height:auto; border:0;">`);
				this.siteService.setMessage('Image inserted at your cursor.', 'success', 4000);
			},
			error: () => this.siteService.setMessage('Image upload failed.', 'error')
		});
	}

	/**
	 * Server-side import: wraps fragments, decodes URL-encoded tokens, copies every image onto
	 * One Morning storage and rewrites the references, and lints. Replaces the editor content.
	 */
	private import(html: string) {
		if (!html?.trim() || this.importing) return;
		this.importing = true;
		this.dataService.importEmailHtml(html).subscribe({
			next: res => {
				this.importing = false;
				this.html = res.html;
				this.htmlChange.emit(res.html);
				this.changed.emit();
				this.issues = res.issues ?? [];
				this.images = res.images ?? [];
				const hosted = this.images.filter(i => i.hosted).length;
				const failed = this.images.filter(i => i.error).length;
				const errors = this.errorCount;
				const parts = [];
				if (hosted) parts.push(`${hosted} image${hosted === 1 ? '' : 's'} copied to One Morning storage`);
				if (failed) parts.push(`${failed} could not be hosted`);
				// Blocking errors mean it can't be saved yet, so say so instead of a plain success.
				if (errors) parts.push(`${errors} error${errors === 1 ? '' : 's'} to fix before saving`);
				// Only report when something happened; a paste that changed nothing (e.g. one URL) stays quiet.
				if (parts.length) this.siteService.setMessage(`Imported. ${parts.join('; ')}.`, failed || errors ? 'warn' : 'success', 5000);
			},
			error: err => {
				this.importing = false;
				this.siteService.setMessage(err?.error?.title ?? err?.error ?? 'Import failed.', 'error', 6000);
			}
		});
	}
}
